import { Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { reportChartLineOptions } from '../highcharts-options';
import { QuickBuildService, UIFilter } from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { departments, levels, ReportVizChartColors } from '../constants';
import { Log } from 'src/app/logger.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { StreamsSubscribeDrawerService } from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { DemographicsConfig } from '../interfaces/report-config.interfaces';
import { SearchFilter } from 'ldt-people-api';
import * as dayjs from 'dayjs';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const FIELDS_TO_GROUP_BY = ['jobs.function', 'jobs.level'];
const REPORT_NAME = 'demographics';

@Component({
    selector: 'app-reports-demographics',
    templateUrl: './demographics.component.html',
    styleUrls: ['./demographics.component.scss'],
    standalone: false
})
export class DemographicsComponent extends BaseReportComponent<DemographicsConfig> {
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() field: string;

  @Input() maxSeries: number = 25;
  @Input() allowFieldSelection = true;
  @Input() boxShadow: boolean = true;

  @Input()
  set breakdownByOptions(fields: string[]) {
    this._breakdownByOptions = fields || FIELDS_TO_GROUP_BY;
    this.breakdownByToggleOptions = this._breakdownByOptions.map((field) => ({
      value: field,
      displayText: this.getFieldDisplayName(field),
    }));
  }
  get breakdownByOptions(): string[] {
    return this._breakdownByOptions;
  }
  breakdownByToggleOptions: ToggleOption[] = [];
  private _breakdownByOptions: string[] = FIELDS_TO_GROUP_BY;

  @Input()
  set selectedBreakdownBy(value: string | undefined) {
    if (value) {
      this._selectedBreakdownBy = value;
    }
  }
  get selectedBreakdownBy(): string {
    return this._selectedBreakdownBy;
  }

  @Input()
  set selectedValueType(value: string | undefined) {
    if (value) {
      this._selectedValueType = value;
    }
  }
  get selectedValueType(): string {
    return this._selectedValueType;
  }

  private _selectedBreakdownBy: string = FIELDS_TO_GROUP_BY[0];

  valueTypeOptions: ToggleOption[] = [
    { value: 'percentage', displayText: '%' },
    { value: 'number', displayText: '#' },
  ];
  private _selectedValueType: string = 'percentage';

  protected readonly CHART_ID = 'demographics-over-time';
  protected readonly CHART_DATA_DESC = 'Demographic data over time';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsSubscribeDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsSubscribeDrawerService,
      auth,
      statsigService,
      layoutService
    );

    // Initialize toggle options
    this.breakdownByToggleOptions = this._breakdownByOptions.map((field) => ({
      value: field,
      displayText: this.getFieldDisplayName(field),
    }));

    if (!this.dateFrom || !this.dateTo) {
      [this.dateFrom, this.dateTo] = this.getDefaultDateRange();
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedBreakdownBy', () => this._selectedBreakdownBy);
    this.registerConfigProperty('selectedValueType', () => this._selectedValueType);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartLineOptions)
    );
    this.chart.update(demographicsChartNoSeries, true, true);
    this.initializeContextMenu();
  }

  getChartData() {
    if (!this.quickBuild) return;

    const reportParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: [this._selectedBreakdownBy],
    };
    this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        this.quickBuild
      )
      .subscribe({
        next: (data) => {
          Log.d('DemographicsComponent getChartData demographics data', data);
          const series = this.generateChartSeries(
            data.data.demographics,
            this._selectedBreakdownBy
          );
          this.updateChart(
            series,
            undefined,
            'Employee Count by ' + this.getFieldDisplayName(this._selectedBreakdownBy)
          );
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('DemographicsComponent getChartData error', error);
          this.showChartError(error);
        },
      });
  }

  onBreakdownByChanged(value: string) {
    if (!value) return;

    this._selectedBreakdownBy = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  generateChartSeries(inputData: any, byType: string): any[] {
    // The API returns a ton of data sometimes, we filter to just the maxSeries number
    // based on the top counts of data
    const totalKeyCounts: Record<string, number> = {};
    const firstValues: Record<string, number> = {};

    let processed = inputData.reduce((acc: any, d: any) => {
      const keyVal = d['group_values'][0].value;

      // Filter out the current company
      if (
        this._selectedBreakdownBy === 'jobs.metadata.previous_job.company.name' &&
        keyVal === this.quickBuild?.name
      ) {
        return acc;
      }

      if (!acc[keyVal]) {
        acc[keyVal] = {};
      }

      if (!totalKeyCounts[keyVal]) {
        totalKeyCounts[keyVal] = 0;
      }

      if (!firstValues[keyVal]) {
        firstValues[keyVal] = d.count_employees;
      }

      totalKeyCounts[keyVal] += d.count_employees;
      acc[keyVal][d.date] = d.count_employees;

      return acc;
    }, {});

    // Find the top 25 keys by count
    const topKeys = Object.entries(totalKeyCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, this.maxSeries)
      .map(([key, _]) => key);

    const indexer =
      byType === 'jobs.function'
        ? (val: string) => departments.indexOf(val)
        : (val: string) => levels.indexOf(val);

    return Object.entries(processed)
      .filter(([key, _]) => topKeys.includes(key))
      .map(([key, value]) => {
        return {
          id: key,
          name: key,
          data: Object.entries(value as Record<string, number>).map(([date, count]) => [
            new Date(date!).valueOf(),
            this._selectedValueType === 'percentage'
              ? ((count - firstValues[key]) / firstValues[key]) * 100
              : count,
          ]),
          color: ReportVizChartColors[indexer(key)],
          type: 'line',
        };
      });
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const date = new Date(point.x as number);
    const series = point.series;
    if (!this.quickBuild) {
      Log.s('DemographicsComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
      });
      return [];
    }

    Log.d('DemographicsComponent getContextMenuItems', point);

    // Fix for weird previous company situation
    let breakdownBy = this._selectedBreakdownBy;
    if (breakdownBy === 'jobs.metadata.previous_job.company.name') {
      breakdownBy = 'jobs.previous_job.company.name';
    }

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    const activeAtFilter: UIFilter = {
      filter: {
        field: 'job_active_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
      },
    };
    const categoryFilter: UIFilter = {
      filter: {
        field: breakdownBy,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [series.name],
      },
    };

    Log.d('DemographicsComponent getContextMenuItems categoryFilter', categoryFilter);

    QuickBuildService.addFiltersToFilterGroup(qbCopy, [activeAtFilter], 'moneyball');
    if (this._selectedBreakdownBy.startsWith('jobs')) {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], 'moneyball');
    } else {
      QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], '__root__');
    }

    return [
      {
        label: `View ${series.name} employees that worked at ${this.quickBuild.name} in ${dayjs(date).utc().format('MMM YYYY')}`,
        icon: 'group',
        action: (p) => {
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          qbCopy.name = `${series.name} employees that worked at ${this.quickBuild.name} in ${dayjs(date).utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  async resetChartData() {
    switch (this._selectedValueType) {
      case 'percentage':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Pct Change Over Timespan' },
          },
        });
        break;
      case 'number':
        await this.updateChart([], undefined, undefined, {
          yAxis: {
            title: { text: 'Number of Employees' },
          },
        });
        break;
    }

    await super.resetChartData();
  }

  toggleValueType(value: string) {
    if (!value) return;

    Log.d('DemographicsComponent toggleValueType', value);
    this._selectedValueType = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}

const demographicsChartNoSeries: Highcharts.Options = {
  title: {
    text: 'Person Count',
  },
  legend: {
    itemWidth: 150,
    maxHeight: 90,
  },
  yAxis: [
    {
      title: {
        text: 'Pct Change Over Timespan',
      },
    },
  ],
};
